import { GetReturnErrorCode } from "@redotech/customer-portal-sdk/rpc/schema/get-return";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useReturnConfirmation } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-return-confirmation";
import { fireReturnConfirmationLoadedEvent } from "@redotech/redo-customer-portal-shared/window-events/confirmation-loaded";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { useContext, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SettingsContext } from "../../contexts/settings";
import { UserContext } from "../../contexts/user";
import { ItemDetail } from "../shared-components/item-detail";
import { Addresses } from "../submission/components/addresses";
import { Card } from "../submission/components/card";
import { AlertsCard } from "./components/alerts-card";
import { CreationStatus } from "./components/creation-status";
import { DeadlineCard } from "./components/deadline-card";
import { ManualLabelCard } from "./components/manual-label-card";
import { ManualLabelPackingSlipButtons } from "./components/manual-label-packing-slip-buttons";
import { MaybeDisplayHtml } from "./components/maybe-display-html";
import { PackagePickupModals } from "./components/package-pickup-modals";
import { ReturnItems } from "./components/return-items";
import { ReturnStepsCard } from "./components/return-steps-card";
import { SummaryCardConfirmation } from "./components/summary-card-confirmation";
import { TopCard } from "./components/top-card";
import { WarningsCard } from "./components/warnings-card";
import * as detailCss from "./confirmation.module.css";
import { Spinner } from "./spinner";

export function ReturnConfirmation() {
  const returnId = useParams().returnId || "";
  const [searchParams] = useSearchParams();

  const settings = useContext(SettingsContext);
  const user = useContext(UserContext);

  // Check for boxlessReturnsLocations URL parameter
  const shouldAutoOpenBoxlessLocations = useMemo(() => {
    return searchParams.get("boxlessReturnsLocations") === "true";
  }, [searchParams]);
  // Check for fedExEasyReturnsLocations URL parameter
  const shouldAutoOpenFedexLocations = useMemo(() => {
    return searchParams.get("fedExEasyReturnsLocations") === "true";
  }, [searchParams]);
  const {
    loadingReturn,
    error,
    errorCodeData,
    returnHeader,
    steps,
    stepResource,
    rejected,
    rejectHtml,
    customSuccessMessage,
    warnings,
    deadlineDays,
    showDeadline,
    products,
    currency,
    newItems,
    newItemsHeader,
    showAddresses,
    newOrderAddress,
    returnAddress,
    returnTotals,
    summaryFeesHeader,
    summaryFeesOrderHeader,
    texts,
    labelDeducted,
    showCreationStatus,
    creationStatus,
    returnType,
    shouldShowSummary,
    manualLabelInfo,
    manualLabelData,
    showReturnSteps,
    upsellBillingTrigger,
    pickupModals,
  } = useReturnConfirmation({
    settings,
    returnId,
    navigateToCheckout: (url: string) => {
      window.open(url, "_blank");
    },
    isMerchantCreated: user?.isMerchant ?? false,
  });

  const screen = useScreenType();

  const shouldShowDesktopView = screen === ScreenType.DESKTOP;

  useEffect(() => {
    if (!showCreationStatus && returnId) {
      fireReturnConfirmationLoadedEvent(returnId);
    }
  }, [showCreationStatus, returnId]);

  const SummaryCard = useMemo(
    () =>
      returnTotals ? (
        <Flex flexDirection="column" pb="4xl">
          <SummaryCardConfirmation
            currency={currency}
            feesHeader={summaryFeesHeader}
            feesOrderHeader={summaryFeesOrderHeader}
            labelDeducted={labelDeducted}
            newItems={newItems}
            products={products}
            settings={settings}
            totals={returnTotals}
            upsellBillingTrigger={upsellBillingTrigger}
          />
        </Flex>
      ) : null,
    [
      labelDeducted,
      returnTotals,
      currency,
      summaryFeesHeader,
      summaryFeesOrderHeader,
      newItems,
      products,
      settings,
      upsellBillingTrigger,
    ],
  );

  const AddressesComponent = useMemo(() => {
    const addresses = [];
    if (newOrderAddress) {
      addresses.push(newOrderAddress);
    }
    if (returnAddress) {
      addresses.push(returnAddress);
    }
    return showAddresses ? (
      <Flex data-ui="addresses" flexDirection="column" gap="lg" pt="md">
        <Text fontWeight="bold">Shipping information</Text>
        <Addresses addresses={addresses} />
      </Flex>
    ) : null;
  }, [showAddresses, newOrderAddress, returnAddress]);
  //Error code handling
  if (errorCodeData) {
    switch (errorCodeData.type) {
      case GetReturnErrorCode.ReturnNotFound:
        return (
          <Flex flexDirection="column" gap="sm" p="md">
            <Text fontWeight="bold" textAlign="center">
              {errorCodeData?.data.texts.title}
            </Text>
          </Flex>
        );
      default:
        assertNever(errorCodeData.type);
    }
  }
  if (error) {
    return <Text>{error}</Text>;
  }
  if (loadingReturn) {
    return (
      <Card fullscreen>
        <Flex
          align="center"
          data-ui="loading"
          flexDirection="column"
          gap="md"
          grow={1}
          justify="center"
        >
          <Spinner />
          <div style={{ height: "50px" }} />
        </Flex>
      </Card>
    );
  }

  if (rejected) {
    return (
      <Flex data-ui="rejected" flexDirection="column" gap="sm" p="md">
        <Text fontWeight="bold" textAlign="center">
          {texts.rejectedText}
        </Text>
        <MaybeDisplayHtml html={rejectHtml} />
      </Flex>
    );
  }

  // Show creation status (handles both TypeAnimation and progress bar phases)
  if (showCreationStatus) {
    return (
      <CreationStatus
        creationStatus={creationStatus}
        returnType={returnType}
        showCreationStatus={showCreationStatus}
      />
    );
  }

  return (
    <>
      <Card data-ui="return-confirmation" fullscreen>
        <Flex flexDirection="column" gap="2xl" grow={1} pb="4xl">
          <Flex
            className={
              shouldShowDesktopView ? detailCss.outlinedCard : undefined
            }
            flexDirection="column"
            gap="2xl"
            pb={shouldShowDesktopView ? "3xl" : "none"}
            pt="3xl"
            px={shouldShowDesktopView ? "3xl" : "none"}
          >
            <TopCard
              action={returnHeader.onClick}
              actionTitle={returnHeader.onClickTitle}
              status={returnHeader.status}
              subtitle={returnHeader.subtitle}
              title={returnHeader.title}
            />

            {customSuccessMessage && (
              <MaybeDisplayHtml html={customSuccessMessage} />
            )}

            <AlertsCard alerts={returnHeader.alerts} />

            <Flex data-ui="steps-section" flexDirection="column" gap="lg">
              <Divider />
              {warnings && warnings.length > 0 && (
                <WarningsCard warnings={warnings} />
              )}
              {manualLabelInfo.showManualLabel && (
                <ManualLabelCard instructions={manualLabelInfo.instructions} />
              )}
              {showReturnSteps && (
                <ReturnStepsCard
                  copyResource={stepResource}
                  pickupModals={pickupModals}
                  returnId={returnId}
                  shouldAutoOpenBoxlessLocations={
                    shouldAutoOpenBoxlessLocations
                  }
                  shouldAutoOpenFedexLocations={shouldAutoOpenFedexLocations}
                  steps={steps}
                />
              )}
              {manualLabelInfo.showManualLabel && manualLabelData && (
                <ManualLabelPackingSlipButtons
                  manualLabelData={manualLabelData}
                />
              )}
              {showDeadline && <DeadlineCard deadlineDays={deadlineDays} />}
              {!shouldShowDesktopView && <Divider />}
            </Flex>
          </Flex>
          {shouldShowSummary && (
            <Flex
              data-ui="summary-section"
              flexDirection="column"
              gap="2xl"
              px={shouldShowDesktopView ? "3xl" : "none"}
            >
              {shouldShowDesktopView && SummaryCard}
              <Flex flexDirection="column" gap="3xl">
                <ReturnItems currency={currency} products={products} />
                <Divider />
                {newItems && newItems?.length > 0 && (
                  <Flex
                    data-ui="exchange-items"
                    flexDirection="column"
                    gap="lg"
                  >
                    <Flex flexDirection="column" gap="xl">
                      <Text fontSize="lg" fontWeight="semibold">
                        {newItemsHeader}
                      </Text>
                      {newItems?.map((item, i) => (
                        <ItemDetail item={item} key={item.title + i} />
                      ))}
                    </Flex>
                    <Divider />
                  </Flex>
                )}
              </Flex>
              {AddressesComponent}
              {!shouldShowDesktopView && SummaryCard}
            </Flex>
          )}
        </Flex>
      </Card>
      <PackagePickupModals pickupModals={pickupModals} />
    </>
  );
}
